/*
 * Copyright (c) 2024. Tooling Team
 */

import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { Checkbox } from "@uhg-abyss/web/ui/Checkbox";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { Tooltip } from "@uhg-abyss/web/ui/Tooltip";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { getRecordRowId } from "../../utils/funcs";
import DescriptionCellRenderer from "./DescriptionCellRenderer";
import { buildEipGridColumns } from "./eipGridColumnConfig";
import LongDescriptionCellRenderer, {
  formatLongDescription,
} from "./LongDescriptionCellRenderer";
import { getMappingStatusFilterOptions } from "./mappingStatusOptions";
import {
  createDownloadDropdownItems,
  MvdClearFiltersToolbar,
  useDefaultAdvancedFilterInput,
} from "./mvdDataTableUtils";
import { getMvdRowState, ROW_STATE_LABELS } from "./rowState";
import RowStateCellRenderer from "./RowStateCellRenderer";
import TagsCellRenderer from "./TagsCellRenderer";

const INTERNAL_ROW_ID = "__mvdRowId";
const SELECT_COLUMN_ID = "abyss-select";
const ROW_STATE_COLUMN_ID = "__mvdRowState";
const LOCAL_ADDITIONAL_DETAILS_COLUMN_ID = "__mvdLocalAdditionalDetails";
const STANDARD_ADDITIONAL_DETAILS_COLUMN_ID = "__mvdStandardAdditionalDetails";
const PENDING_BULK_EDIT_TOOLTIP =
  "Please save pending changes before bulk editing";
const STANDARD_DESCRIPTION_COL_ACCESSOR = "standardDescription";
const LOCAL_DESCRIPTION_COL_ACCESSOR = "localDescription";

const ROW_HIGHLIGHT_COLORS = {
  added: "#e7f1ff",
  deleted: "#e9ecef",
  new: "#e7f1ff",
  updated: "#fcedde",
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const ROW_STATE_FILTER_OPTIONS = [
  { label: ROW_STATE_LABELS.added, value: "added" },
  { label: ROW_STATE_LABELS.updated, value: "updated" },
  { label: ROW_STATE_LABELS.deleted, value: "deleted" },
  { label: ROW_STATE_LABELS.new, value: "new" },
];
const STATUS_FILTER_OPTIONS = getMappingStatusFilterOptions();

const CSV_ADDITIONAL_DETAILS_COLUMNS = {
  localDescription: {
    id: LOCAL_ADDITIONAL_DETAILS_COLUMN_ID,
    header: "Local Additional Details",
  },
  standardDescription: {
    id: STANDARD_ADDITIONAL_DETAILS_COLUMN_ID,
    header: "Standard Additional Details",
  },
};

const DEFAULT_SELECTION_SOURCE = {
  source: "none",
  isSelectAllChecked: false,
  hasActiveFilter: false,
};

//helper function for local/standard prefixed titles
const renderSectionHeader = (section, label) => (
  <span className="mvd-column-header" aria-label={`${section} ${label}`}>
    <span className="mvd-column-header__section">{section}</span>
    <span className="mvd-column-header__label">{label}</span>
  </span>
);

//helper function for spacing non-local/standard prefixed titles correctly
const renderUnsectionedHeader = (label) => (
  <span className="mvd-column-header" aria-label={label}>
    <span className="mvd-column-header__section mvd-column-header__section--empty">
      Section
    </span>
    <span className="mvd-column-header__label">{label}</span>
  </span>
);

const areRowIdArraysEqual = (left, right) => {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
};

const hasSelectableRowId = (displayRows, rowId, isBaseRowSelectable) => {
  for (let index = 0; index < displayRows.length; index += 1) {
    const row = displayRows[index];

    if (row[INTERNAL_ROW_ID] === rowId && isBaseRowSelectable(row)) {
      return true;
    }
  }

  return false;
};

const getNextSelectedRowIds = ({
  prevSelectedRowIds,
  displayRows,
  isBaseRowSelectable,
  pendingRecordIds,
}) => {
  const nextSelectedRowIds = [];

  for (let index = 0; index < prevSelectedRowIds.length; index += 1) {
    const rowId = prevSelectedRowIds[index];

    if (hasSelectableRowId(displayRows, rowId, isBaseRowSelectable)) {
      nextSelectedRowIds.push(rowId);
    }
  }

  if (pendingRecordIds.size > 0 && nextSelectedRowIds.length > 1) {
    return nextSelectedRowIds.slice(0, 1);
  }

  return nextSelectedRowIds;
};

const addInternalRowFields = (rows) =>
  rows.map((row, index) => ({
    ...row,
    [INTERNAL_ROW_ID]: getRecordRowId(row) ?? `row-${index}`,
    [ROW_STATE_COLUMN_ID]: getMvdRowState(row),
  }));

export function EIPGrid(props) {
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectionSource, setSelectionSource] = useState(
    DEFAULT_SELECTION_SOURCE
  );
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState([
    {
      id: "lastEditedAt",
      desc: true,
    },
  ]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [showFullStandardDescription, setShowFullStandardDescription] =
    useState(false);
  const [showFullLocalDescription, setShowFullLocalDescription] =
    useState(false);

  const handleColumnFiltersChange = (updater) => {
    setColumnFilters(updater);
    setPagination((currentPagination) => ({
      ...currentPagination,
      pageIndex: 0,
    }));
  };

  const allRows = useMemo(
    () =>
      addInternalRowFields(Array.isArray(props.rowData) ? props.rowData : []),
    [props.rowData]
  );

  const sourceRows = useMemo(() => {
    if (Array.isArray(props.focusedRows) && props.focusedRows.length > 0) {
      return props.focusedRows;
    }

    return Array.isArray(props.rowData) ? props.rowData : [];
  }, [props.focusedRows, props.rowData]);

  const displayRows = useMemo(
    () => addInternalRowFields(sourceRows),
    [sourceRows]
  );

  const pendingRecordIds = useMemo(() => {
    const records = Array.isArray(props.updatedRecords)
      ? props.updatedRecords
      : [];

    return new Set(
      records
        .map((record) => getRecordRowId(record))
        .filter((recordId) => recordId !== undefined)
    );
  }, [props.updatedRecords]);

  const selectedRowIdSet = useMemo(
    () => new Set(selectedRowIds),
    [selectedRowIds]
  );

  useEffect(() => {
    setShowFullStandardDescription(
      columnSizing[STANDARD_DESCRIPTION_COL_ACCESSOR] > 250
    );
    setShowFullLocalDescription(
      columnSizing[LOCAL_DESCRIPTION_COL_ACCESSOR] > 250
    );
  }, [columnSizing]);

  const lockedRowId = selectedRowIds[0];
  const hasPendingRecordChanges =
    pendingRecordIds.size > 0 && lockedRowId !== undefined;
  const showSelection =
    !props.isAllStandardCodeSystem &&
    props.mapStatus !== "REVIEW" &&
    props.isEditableVersion !== false;
  const hasActiveFocusFilter =
    (props.currentFocus && Object.keys(props.currentFocus).length > 0) ||
    (Array.isArray(props.focusedRows) && props.focusedRows.length > 0);

  const isBaseRowSelectable = (record) => {
    if (!record || !showSelection) {
      return false;
    }

    return !(
      record.delete ||
      (record.flags && record.flags.deleted) ||
      record.isNew
    );
  };

  const isLockedRow = (record) => {
    const recordId = record?.[INTERNAL_ROW_ID] ?? getRecordRowId(record);
    return recordId !== undefined && recordId === lockedRowId;
  };

  const isRowSelectable = (record) =>
    isBaseRowSelectable(record) &&
    (!hasPendingRecordChanges || isLockedRow(record));

  const stripInternalRowId = (record) => {
    const cleanRecord = { ...record };
    delete cleanRecord[INTERNAL_ROW_ID];
    delete cleanRecord[ROW_STATE_COLUMN_ID];
    return cleanRecord;
  };

  const getSelectedRows = () =>
    displayRows
      .filter((row) => selectedRowIdSet.has(row[INTERNAL_ROW_ID]))
      .map(stripInternalRowId);

  useEffect(() => {
    setSelectedRowIds((prevSelectedRowIds) => {
      const nextSelectedRowIds = getNextSelectedRowIds({
        prevSelectedRowIds,
        displayRows,
        isBaseRowSelectable,
        pendingRecordIds,
      });

      if (areRowIdArraysEqual(nextSelectedRowIds, prevSelectedRowIds)) {
        return prevSelectedRowIds;
      }

      return nextSelectedRowIds;
    });
  }, [displayRows, pendingRecordIds]);

  useEffect(() => {
    const selectedRows = getSelectedRows();
    const allSelectableRows = allRows.filter(isBaseRowSelectable);
    const selectedSelectableCount = allSelectableRows.filter((row) =>
      selectedRowIdSet.has(row[INTERNAL_ROW_ID])
    ).length;
    const hasActiveFilter =
      selectionSource.hasActiveFilter || hasActiveFocusFilter;
    const totalSelectableCount = allSelectableRows.length;
    const useAllMapTarget =
      selectionSource.source === "selectAll" &&
      selectionSource.isSelectAllChecked &&
      !hasActiveFilter &&
      selectedSelectableCount === totalSelectableCount &&
      totalSelectableCount > 0;

    props.onSelectedRowsChange?.(selectedRows, {
      source: selectionSource.source,
      isSelectAllChecked: selectionSource.isSelectAllChecked,
      hasActiveFilter,
      selectedSelectableCount,
      totalSelectableCount,
      useAllMapTarget,
    });
    props.editRowsButtonDisabledHandler?.(selectedRows.length < 1);
    props.bulkDeleteButtonDisabledHandler?.(selectedRows.length < 1);
  }, [
    allRows,
    displayRows,
    hasActiveFocusFilter,
    selectedRowIds,
    selectionSource,
  ]);

  const toggleRowSelection = (row, isChecked) => {
    if (!isRowSelectable(row)) {
      return;
    }

    setSelectionSource({
      source: "row",
      isSelectAllChecked: false,
      hasActiveFilter: false,
    });
    setSelectedRowIds((prevSelectedRowIds) => {
      const rowId = row[INTERNAL_ROW_ID];
      if (isChecked) {
        return prevSelectedRowIds.includes(rowId)
          ? prevSelectedRowIds
          : [...prevSelectedRowIds, rowId];
      }

      return prevSelectedRowIds.filter(
        (selectedRowId) => selectedRowId !== rowId
      );
    });
  };

  const setFilteredRowsSelected = (table, isChecked) => {
    const selectableRowIds = table
      .getFilteredRowModel()
      .rows.map((row) => row.original)
      .filter(isRowSelectable)
      .map((row) => row[INTERNAL_ROW_ID]);
    const columnFilters = table.getState?.()?.columnFilters;
    const hasActiveColumnFilter =
      Array.isArray(columnFilters) && columnFilters.length > 0;

    setSelectionSource({
      source: "selectAll",
      isSelectAllChecked: isChecked,
      hasActiveFilter: hasActiveFocusFilter || hasActiveColumnFilter,
    });

    setSelectedRowIds((prevSelectedRowIds) => {
      if (isChecked) {
        return Array.from(
          new Set([...prevSelectedRowIds, ...selectableRowIds])
        );
      }

      return prevSelectedRowIds.filter(
        (selectedRowId) => !selectableRowIds.includes(selectedRowId)
      );
    });
  };

  const getFormattedDateValue = (row) =>
    row.lastEditedAt
      ? moment(row.lastEditedAt).format("MM-DD-YYYY  HH:mm:ss")
      : "";

  const getCsvDateValue = (value) => {
    if (!value) {
      return "";
    }

    const parsedDate = moment(value);
    return parsedDate.isValid()
      ? parsedDate.format("YYYY-MM-DD HH:mm:ss")
      : value;
  };

  const getAdditionalDetailsValue = (...values) => {
    const detailValue = values.find((value) => {
      if (typeof value === "string") {
        return value.trim() !== "";
      }

      return value !== null && value !== undefined;
    });

    if (detailValue === undefined) {
      return "";
    }

    const formattedValue = formatLongDescription(detailValue);
    return formattedValue === "None" ? "" : formattedValue;
  };

  const getCsvAdditionalDetailsValue = (row, columnId) => {
    const record = row.original || {};

    if (columnId === LOCAL_ADDITIONAL_DETAILS_COLUMN_ID) {
      return getAdditionalDetailsValue(
        record.localLongDescription,
        record.longDescription,
        record.sourceConcept?.localLongDescription,
        record.sourceConcept?.longDescription
      );
    }

    if (columnId === STANDARD_ADDITIONAL_DETAILS_COLUMN_ID) {
      return getAdditionalDetailsValue(
        record.standardLongDescription,
        record.targetConcept?.standardLongDescription
      );
    }

    return "";
  };

  const renderCell = (cell, field, content, options = {}) => {
    const row = cell.row.original;
    const value = content ?? row[field] ?? "";
    const rowState = getMvdRowState(row);
    const classNames = ["mvd-grid-cell"];

    if (rowState === "deleted") {
      classNames.push("mvd-grid-cell--deleted");
    }

    if (options.showUpdatedMarker !== false && isMvdCellUpdated(row, field)) {
      classNames.push("mvd-grid-cell--updated");
    }

    return (
      <div
        className={classNames.join(" ")}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </div>
    );
  };

  const renderDescriptionCell = (cell, field, Renderer, showFullContent) =>
    renderCell(
      cell,
      field,
      <Renderer
        column={{ colId: field }}
        data={cell.row.original}
        showFullContent={showFullContent}
      />
    );

  const renderTagsCell = (cell) =>
    renderCell(
      cell,
      "tags",
      <TagsCellRenderer
        colDef={{
          cellClassRules: {
            "cell-updated": () => isMvdCellUpdated(cell.row.original, "tags"),
          },
        }}
        data={cell.row.original}
      />,
      {
        showUpdatedMarker: false,
      }
    );

  const selectionColumn = useMemo(() => {
    if (!showSelection) {
      return [];
    }

    return [
      {
        id: SELECT_COLUMN_ID,
        accessorKey: SELECT_COLUMN_ID,
        header: ({ table }) => {
          const selectableRows = table
            .getFilteredRowModel()
            .rows.map((row) => row.original)
            .filter(isRowSelectable);
          const selectableRowIds = selectableRows.map(
            (row) => row[INTERNAL_ROW_ID]
          );
          const selectedCount = selectableRowIds.filter((rowId) =>
            selectedRowIdSet.has(rowId)
          ).length;
          const allSelected =
            selectedCount === selectableRowIds.length &&
            selectableRowIds.length > 0;
          const isIndeterminate =
            selectedCount > 0 && selectedCount < selectableRowIds.length;
          const checkbox = (
            <Checkbox
              label="Select all rows"
              hideLabel
              isChecked={allSelected}
              isDisabled={
                hasPendingRecordChanges || selectableRows.length === 0
              }
              isIndeterminate={isIndeterminate}
              onChange={(event) =>
                setFilteredRowsSelected(table, event.target.checked)
              }
              controlIds={selectableRowIds.map((rowId) => `mvd-row-${rowId}`)}
            />
          );

          return hasPendingRecordChanges ? (
            <Tooltip content={PENDING_BULK_EDIT_TOOLTIP}>{checkbox}</Tooltip>
          ) : (
            checkbox
          );
        },
        cell: (cell) => {
          const row = cell.row.original;
          const disabled = !isRowSelectable(row);
          const checkbox = (
            <Checkbox
              label="Select row"
              hideLabel
              inputId={`mvd-row-${row[INTERNAL_ROW_ID]}`}
              isChecked={selectedRowIdSet.has(row[INTERNAL_ROW_ID])}
              isDisabled={disabled}
              onChange={(event) =>
                toggleRowSelection(row, event.target.checked)
              }
            />
          );

          return disabled && hasPendingRecordChanges ? (
            <Tooltip content={PENDING_BULK_EDIT_TOOLTIP}>{checkbox}</Tooltip>
          ) : (
            checkbox
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          headerLabel: "Select rows",
          textAlign: "center",
        },
        size: 40,
        maxSize: 40,
        minSize: 40,
        enableResizing: false,
      },
    ];
  }, [
    hasActiveFocusFilter,
    hasPendingRecordChanges,
    selectedRowIdSet,
    showSelection,
  ]);

  // Special-cased by the API's column `key` (see eipGridColumnConfig.js) only
  // for rendering behavior the API response can't express (custom widgets,
  // date formatting). Do NOT add a new column here - new columns must come
  // from mapElements.columns (via props.mapColumns/deriveMapColumnSections in
  // utils/funcs.js), not be hardcoded in this component.
  const getCellRenderer = (key, accessorKey) => {
    if (key === "localDescription") {
      return (cell) =>
        renderDescriptionCell(
          cell,
          accessorKey,
          DescriptionCellRenderer,
          showFullLocalDescription
        );
    }

    if (key === "standardCodeDescription") {
      return (cell) =>
        renderDescriptionCell(
          cell,
          accessorKey,
          LongDescriptionCellRenderer,
          showFullStandardDescription
        );
    }

    if (key === "lastEditedAt") {
      return (cell) =>
        renderCell(cell, accessorKey, getFormattedDateValue(cell.row.original));
    }

    if (key === "tags") {
      return renderTagsCell;
    }

    return (cell) => renderCell(cell, accessorKey);
  };

  const getAccessorFn = (key, accessorKey) => {
    if (key === "lastEditedAt") {
      return (row) =>
        row[accessorKey] ? moment(row[accessorKey]).format("MM/DD/YYYY") : "";
    }

    return undefined;
  };

  const getSortingFn = (key) => {
    if (key === "lastEditedAt") {
      return (rowA, rowB) => {
        const dateA = rowA.original?.lastEditedAt;
        const dateB = rowB.original?.lastEditedAt;

        if (!dateA && !dateB) {
          return 0;
        }

        if (!dateA) {
          return -1;
        }

        if (!dateB) {
          return 1;
        }

        return Math.sign(moment(dateA).diff(moment(dateB)));
      };
    }

    return undefined;
  };

  const getCsvFormatter = (key) => {
    if (key === "lastEditedAt") {
      // The cell's display value is date-only (MM/DD/YYYY); the CSV export
      // needs the full timestamp, which only lives on the original row.
      return ({ value, original }) =>
        getCsvDateValue(original?.lastEditedAt ?? value);
    }

    if (key === "tags") {
      return ({ value }) =>
        Array.isArray(value)
          ? value
              .map((tag) => `${tag.name}: ${(tag.values || []).join(", ")}`)
              .join("; ")
          : "";
    }

    return undefined;
  };

  // apiDrivenColumns is the single source of Local/Standard/Status/Comments/
  // date/Tags columns - order (including where Tags lands), section grouping
  // and label all come straight from props.mapColumns (ultimately
  // mapElements.columns via deriveMapColumnSections in utils/funcs.js). Do
  // not push additional hardcoded column objects into `columns` below for
  // data that should come from the backend; if a column is missing from the
  // API response, that's a backend contract gap (see MANUALLY_ADDED_COLUMNS
  // in utils/funcs.js for the documented, temporary exceptions).
  const apiDrivenColumns = useMemo(
    () =>
      buildEipGridColumns(props.mapColumns, {
        renderSectionHeader,
        renderUnsectionedHeader,
        getCellRenderer,
        getCsvFormatter,
        getAccessorFn,
        getSortingFn,
      }),
    [props.mapColumns, showFullLocalDescription, showFullStandardDescription]
  );

  // Select/State are the only legitimate frontend-only columns - they have
  // no backing entry in mapElements.columns by design (selection UI, derived
  // row state). Everything else, including Tags, must come from
  // apiDrivenColumns; don't grow this list with more hardcoded columns.
  // DO NOT MANUALLY ADD COLUMNS HERE. NEW COLUMNS MUST COME FROM
  // props.mapColumns (mapElements.columns / deriveMapColumnSections IN
  // utils/funcs.js). AI AGENTS AND DEVS: ADDING A HARDCODED COLUMN OBJECT
  // BELOW BREAKS THE DYNAMIC COLUMN ARCHITECTURE AND WILL BE REJECTED.
  const columns = useMemo(
    () => [
      ...selectionColumn,
      {
        id: ROW_STATE_COLUMN_ID,
        accessorKey: ROW_STATE_COLUMN_ID,
        header: renderUnsectionedHeader("State"),
        cell: (cell) => (
          <RowStateCellRenderer node={{ data: cell.row.original }} />
        ),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          headerLabel: "State",
          textAlign: "center",
          customSetCellCsv: ({ value }) => ROW_STATE_LABELS[value] || "",
        },
        size: 100,
      },
      ...apiDrivenColumns,
    ],
    [selectionColumn, apiDrivenColumns]
  );

  const initialColumnOrder = useMemo(
    () => getLeafColumnIds(columns),
    [columns]
  );

  const dataTableProps = useDataTable({
    initialData: displayRows,
    initialColumns: columns,
    rowIdKey: INTERNAL_ROW_ID,
    columnFilterConfig: {
      defaultSettings: {
        filterMode: "advanced",
      },
      individualSettings: {
        [ROW_STATE_COLUMN_ID]: {
          defaultCondition: "equals",
          filterMode: "advanced",
          inputConfig: {
            type: "select",
            isMulti: true,
            options: ROW_STATE_FILTER_OPTIONS,
          },
          conditionMap: [
            { condition: "equals" },
            { condition: "notEqual" },
            { condition: "empty", isSeparated: true },
            { condition: "notEmpty" },
          ],
        },
        status: {
          defaultCondition: "equals",
          inputConfig: {
            type: "select",
            isMulti: true,
            options: STATUS_FILTER_OPTIONS,
          },
          conditionMap: [
            { condition: "equals" },
            { condition: "notEqual" },
            { condition: "empty", isSeparated: true },
            { condition: "notEmpty" },
          ],
        },
        lastEditedAt: {
          inputConfig: {
            type: "date",
          },
        },
      },
    },
    initialStateConfig: {
      initialColumnOrder,
    },
    tableConfig: {
      enableColumnFilters: true,
      enableColumnResizing: true,
      enableSorting: true,
      state: {
        pagination,
        sorting,
        columnFilters,
        columnSizing,
      },
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      onColumnFiltersChange: handleColumnFiltersChange,
      onColumnSizingChange: setColumnSizing,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    },
    defaultSettingsConfig: {
      rowHeight: "compact",
    },
  });

  const downloadDropdownItems = createDownloadDropdownItems({
    additionalColumnMap: CSV_ADDITIONAL_DETAILS_COLUMNS,
    getAdditionalColumnValue: getCsvAdditionalDetailsValue,
  });

  useEffect(() => {
    dataTableProps.setData(displayRows, true);
  }, [displayRows, dataTableProps.setData]);

  useEffect(() => {
    dataTableProps.setColumns(columns, true);
  }, [columns, dataTableProps.setColumns]);

  const rowsHighlighted = useMemo(
    () =>
      displayRows
        .map((row) => {
          const rowState = getMvdRowState(row);

          if (!rowState) {
            return null;
          }

          return {
            rowId: row[INTERNAL_ROW_ID],
            color: ROW_HIGHLIGHT_COLORS[rowState],
          };
        })
        .filter(Boolean),
    [displayRows]
  );

  const hasColumnFilters = columnFilters.length > 0;

  useDefaultAdvancedFilterInput();

  return (
    <div id="mvdGrid" data-testid="dataTable" className="mvd-data-table mb-4">
      <MvdClearFiltersToolbar
        hasColumnFilters={hasColumnFilters}
        onClearFilters={() => handleColumnFiltersChange([])}
      />
      <DataTable
        title="Map Value Definition"
        hideHeader
        tableState={dataTableProps}
      >
        <DataTable.Pagination
          variant="extended"
          showPageSizeDropdown={true}
          className="mt-2 me-2 ms-2"
        />
        <DataTable.SlotWrapper>
          <DataTable.DownloadDropdown
            dropdownMenuItems={downloadDropdownItems}
          />
        </DataTable.SlotWrapper>
        <DataTable.Table
          hiddenColumnMode="remove"
          rowConfig={{
            highlightConfig: {
              rowsHighlighted,
              highlightRowOnHover: true,
            },
          }}
          scrollableFocus
        />
        <DataTable.Pagination
          variant="extended"
          showPageSizeDropdown={true}
          className="m-2"
        />
      </DataTable>
    </div>
  );
}

export { getMvdRowState };

export function isMvdCellUpdated(row, field) {
  return !!row?.flags?.updateHistory?.[field];
}

function getLeafColumnIds(columns) {
  return columns.flatMap((column) => {
    if (column.columns) {
      return getLeafColumnIds(column.columns);
    }

    return [column.id || column.accessorKey].filter(Boolean);
  });
}

EIPGrid.propTypes = {
  mapStatus: PropTypes.string.isRequired,
  rowData: PropTypes.array,
  mapColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      primaryName: PropTypes.string,
      section: PropTypes.string,
    })
  ),
  editRowsButtonDisabledHandler: PropTypes.func,
  bulkDeleteButtonDisabledHandler: PropTypes.func,
  onSelectedRowsChange: PropTypes.func,
  onEditCellClicked: PropTypes.func,
  onDeleteCellClicked: PropTypes.func,
  onSetStatus: PropTypes.func,
  isEditableVersion: PropTypes.bool,
  applySavedGridOptions: PropTypes.bool,
  focusedRows: PropTypes.array,
  currentFocus: PropTypes.object,
  handleFocusChange: PropTypes.func,
  isAllStandardCodeSystem: PropTypes.bool,
  updatedRecords: PropTypes.array,
};
